import React, { useState, useEffect, useRef } from "react";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Stack,
  TextField,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Select,
  MenuItem,
  Paper,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import { styled } from "@mui/system";
import api from "api";
import { useDispatch } from "react-redux";
import { setLoading, setDialog, setHPConfigChanged } from "slices/appSlice";
import ActiveLineOfBusiness from "./components/activeLineOfBusiness";
import { useNavigate } from "react-router-dom";
import { formErrorStrfromObj } from "utils/shared";
import EditHPCreateAdoptionDecisions from "containers/PolicyEnforcementRules/components/EditHPCreateAdoptionDecisions";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

// Reusable section box
const CODE_PATTERN = /^[A-Za-z0-9]{5}$/;
const DATE_FORMAT = "YYYY-MM-DD";

const intialiseDate = (date) => {
  return dayjs([date[0], date[1], date[2]]);
};
const formatDateForPayload = (value) => {
  if (!value.isValid()) {
    throw new Error("Invalid date in one of the PA code records");
  }
  return value.format("YYYY-MM-DDTHH:mm:ss");
};

function SectionBox({
  title,
  items,
  onDelete,
  onAdd,
  onEdit,
  addDisabled = false,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "60vh",
        height: "100%",
        minWidth: 0, // Allows container to shrink
        overflow: "hidden", // Prevents overflow
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          {title}
        </Typography>
        {onAdd && (
          <Button
            variant="text"
            size="small"
            onClick={onAdd}
            disabled={addDisabled}
            sx={{
              minWidth: "auto",
              px: 2,
              fontWeight: "bold",
            }}
          >
            Add
          </Button>
        )}
      </Box>
      <Paper
        role="presentation"
        variant="outlined"
        data-testid="PxCodeList"
        sx={{
          flex: 1,
          maxHeight: "350px",
          minHeight: "350px",
          overflow: "auto",
          padding: 1,
          minWidth: 0, // Allows container to shrink
          width: "100%",
        }}
      >
        {items.length === 0 ? (
          <Typography variant="body2" sx={{ fontSize: "1.1rem" }}>
            No codes added
          </Typography>
        ) : (
          <>
            {/* Header Row */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                width: "100%",
                mb: 1,
                pb: 0.5,
                borderBottom: "1px solid #ccc",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: "bold",
                  width: "30%",
                  minWidth: "60px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Code
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: "bold",
                  width: "25%",
                  minWidth: "70px",
                  textAlign: "center",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Effective Date
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: "bold",
                  width: "25%",
                  minWidth: "70px",
                  textAlign: "center",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                End Date
              </Typography>
              <Box sx={{ width: "20%", minWidth: "80px", flexShrink: 0 }} />
            </Box>

            {/* Data Rows */}
            {items.map((item, index) => (
              <Box
                key={item.id || index}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  width: "100%",
                  mb: 0.1,
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "1rem",
                    width: "30%",
                    minWidth: "60px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.paCode || "-"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "1rem",
                    width: "25%",
                    minWidth: "70px",
                    textAlign: "center",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.effectiveDateTime
                    ? item.effectiveDateTime.format(DATE_FORMAT) || "-"
                    : "-"}{" "}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "1rem",
                    width: "25%",
                    minWidth: "70px",
                    textAlign: "center",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.endDateTime
                    ? item.endDateTime.format(DATE_FORMAT) || "-"
                    : "-"}
                </Typography>
                {onDelete || onEdit ? (
                  <Box
                    sx={{
                      width: "20%",
                      minWidth: "80px",
                      flexShrink: 0,
                      display: "flex",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    {onEdit && (
                      <IconButton
                        aria-label={`edit-${item.paCode}`}
                        size="small"
                        sx={{
                          color: "#616161",
                          "&:hover": {
                            backgroundColor: "rgba(97, 97, 97, 0.1)",
                          },
                        }}
                        onClick={() => onEdit(item)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    {onDelete && (
                      <IconButton
                        aria-label={`delete-${item.paCode}`}
                        size="small"
                        sx={{
                          color: "#B22637",
                          "&:hover": {
                            backgroundColor: "rgba(178, 38, 55, 0.1)",
                          },
                        }}
                        onClick={() => onDelete(item)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ width: "20%", minWidth: "80px" }} />
                )}
              </Box>
            ))}
          </>
        )}
      </Paper>
    </Box>
  );
}

const AddPaCodesDialog = ({ onCancel, onSave }) => {
  const [effectiveDateTime, setEffectiveDateTime] = useState(() => dayjs());
  const [codesText, setCodesText] = useState("");
  const [errors, setErrors] = useState({});

  const handleCancel = () => {
    setEffectiveDateTime(dayjs());
    setCodesText("");
    setErrors({});
    onCancel();
  };

  const handleSave = () => {
    const currentErrors = {};
    if (!effectiveDateTime) {
      currentErrors.effectiveDateTime = "Effective date is required";
    }

    const normalizedCodes = codesText
      .split(/[\s,]+/)
      .map((code) => code.trim().toUpperCase())
      .filter(Boolean);

    if (!normalizedCodes.length) {
      currentErrors.codesText = "List of codes is required";
    }

    const invalidCodes = normalizedCodes.filter(
      (code) => !CODE_PATTERN.test(code)
    );
    if (!currentErrors.codesText && invalidCodes.length) {
      currentErrors.codesText = `Invalid code(s): ${invalidCodes
        .slice(0, 5)
        .join(", ")}`;
    }

    if (Object.keys(currentErrors).length) {
      setErrors(currentErrors);
      return;
    }

    onSave(normalizedCodes, effectiveDateTime);
  };

  return (
    <Dialog open onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Add PA Codes</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Effective Date"
              value={effectiveDateTime}
              onChange={(newDate) => setEffectiveDateTime(newDate)}
              slotProps={{
                textField: {
                  error: Boolean(errors.effectiveDateTime),
                  helperText: errors.effectiveDateTime,
                },
              }}
            />
          </LocalizationProvider>

          <TextField
            label="List of Codes"
            placeholder="Enter 5-character alphanumeric codes separated by commas or new lines"
            multiline
            minRows={6}
            value={codesText}
            onChange={(e) => setCodesText(e.target.value)}
            error={Boolean(errors.codesText)}
            helperText={
              errors.codesText ||
              "Enter 5-character alphanumeric codes separated by commas or new lines."
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EditPaCodeDialog = ({ record, onCancel, onSave, existingCodes = [] }) => {
  const [code, setCode] = useState(record?.paCode || "");
  const [effectiveDateTime, setEffectiveDateTime] = useState(
    record?.effectiveDateTime || dayjs()
  );
  const [errors, setErrors] = useState({});
  const [endDateTime, setEndDateTime] = useState(record?.endDateTime || null);

  if (!record) return null;

  const handleSave = () => {
    const currentErrors = {};
    const normalizedCode = code.trim().toUpperCase();
    if (!normalizedCode) {
      currentErrors.code = "PA code is required";
    } else if (!CODE_PATTERN.test(normalizedCode)) {
      currentErrors.code = "PA code must be 5 alphanumeric characters";
    } else if (
      existingCodes.map((c) => c.toUpperCase()).includes(normalizedCode)
    ) {
      currentErrors.code = "PA code already exists";
    }

    if (!effectiveDateTime) {
      currentErrors.effectiveDateTime = "Effective date is required";
    }

    if (Object.keys(currentErrors).length) {
      setErrors(currentErrors);
      return;
    }

    onSave({
      ...record,
      originalPaCode: record?.paCode,
      paCode: normalizedCode,
      effectiveDateTime,
      endDateTime,
    });
  };

  return (
    <Dialog open onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Edit PA Code</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            label="PA Code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            error={Boolean(errors.code)}
            helperText={errors.code}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Effective Date"
              value={effectiveDateTime}
              onChange={(newDate) => setEffectiveDateTime(newDate)}
              slotProps={{
                textField: {
                  error: Boolean(errors.effectiveDateTime),
                  helperText: errors.effectiveDateTime,
                },
              }}
            />
            <DatePicker
              label="End Date"
              value={endDateTime}
              onChange={(newDate) => setEndDateTime(newDate)}
              slotProps={{
                textField: {
                  error: Boolean(errors.endDateTime),
                  helperText: errors.endDateTime,
                },
              }}
            />
          </LocalizationProvider>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Create a styled TextField component
const CustomTextField = styled((props) => (
  <TextField variant="outlined" fullWidth {...props} />
))({
  "& .MuiInputBase-root": {
    paddingTop: "4px", // Adjust as needed
    paddingBottom: "4px", // Adjust as needed
  },
  "& .MuiInputBase-input": {
    padding: "4px 8px", // Adjust as needed
    lineHeight: "1.5", // Adjust as needed
  },
});
// Create a styled Select component
const CustomSelect = styled((props) => (
  <Select variant="outlined" fullWidth {...props} />
))({
  "& .MuiInputBase-root": {
    paddingTop: "4px", // Adjust as needed
    paddingBottom: "4px", // Adjust as needed
  },
  "& .MuiInputBase-input": {
    padding: "4px 8px", // Adjust as needed
    lineHeight: "1.5", // Adjust as needed
  },
});

// Create a styled Typography component
const CustomTypography = styled((props) => (
  <Typography variant="body1" {...props} />
))({
  minWidth: "130px",
});

const EditHealthPlanModal = ({ hpcKey, handleClose, editable, gridRow }) => {
  const [formData, setFormData] = useState(null);
  const [users, setUsers] = useState([]);
  const [logo, setLogo] = useState(null);
  const [contractTypes, setContractTypes] = useState([]);

  const [logoFile, setLogoFile] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const initialContractTypesRef = useRef(null);
  const tempPaCodeIdRef = useRef(0);

  const [adoptionSaved, setAdoptionSaved] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [paCodes, setPaCodes] = useState([]);
  const [isAddPaDialogOpen, setAddPaDialogOpen] = useState(false);
  const [editingPaCode, setEditingPaCode] = useState(null);

  const getNextTempPaCodeId = () => {
    tempPaCodeIdRef.current += 1;
    return `temp-${tempPaCodeIdRef.current}`;
  };

  const mapPaCodesFromApi = (codes = []) =>
    codes
      ?.filter((item) => item && item.paCode && !item.isDeleted)
      .map((item) => ({
        id: item.nonPolicyPaCodeKey
          ? String(item.nonPolicyPaCodeKey)
          : getNextTempPaCodeId(),
        nonPolicyPaCodeKey: item.nonPolicyPaCodeKey ?? null,
        paCode: (item.paCode || "").toUpperCase(),
        effectiveDateTime: item.effectiveDateTime
          ? intialiseDate(item.effectiveDateTime)
          : "",
        endDateTime: item.endDateTime ? intialiseDate(item.endDateTime) : "",
        isDeleted: false,
        isNew: false,
      })) || [];

  const getContractTypes = async () => {
    try {
      const response = await api.PolicyConfiguration.getPolicyTypeData();
      // Remove "Hybrid" from the response data before setting contractTypes
      const filteredTypes = response.data.filter(
        (type) =>
          type.policyType.toLowerCase() !== "hybrid" && type.policyTypeKey !== 4
      );
      setContractTypes(filteredTypes);
    } catch (error) {
      dispatch(
        setDialog({ message: formErrorStrfromObj(error), title: "Error" })
      );
    }
  };

  const deletePaCode = (record) => {
    if (!record) return;
    let removed = false;
    setPaCodes((prevCodes) => {
      const targetIndex = prevCodes.findIndex(
        (code) => code.id === record.id || code.paCode === record.paCode
      );
      if (targetIndex === -1) {
        return prevCodes;
      }
      const nextCodes = [...prevCodes];
      const target = nextCodes[targetIndex];
      if (!target.nonPolicyPaCodeKey) {
        nextCodes.splice(targetIndex, 1);
      } else {
        nextCodes[targetIndex] = { ...target, isDeleted: true };
      }
      removed = true;
      return nextCodes;
    });
    if (removed) {
      dispatch(setHPConfigChanged({ hpConfigChanged: true }));
    }
  };

  const handlePaCodesSave = (codes, effectiveDateTime) => {
    let hasChanges = false;
    const normalizedDate = dayjs(effectiveDateTime);
    setPaCodes((prev) => {
      const next = [...prev];
      const activeCodes = new Map(
        prev.map((code) => [code.paCode.toUpperCase(), code])
      );
      codes.forEach((code) => {
        const normalizedCode = code.toUpperCase();
        const existing = activeCodes.get(normalizedCode);
        if (existing) {
          if (
            existing.isDeleted ||
            existing.effectiveDateTime !== normalizedDate
          ) {
            const updatedRecord = {
              ...existing,
              isDeleted: false,
              effectiveDateTime: normalizedDate,
            };
            const existingIndex = next.findIndex(
              (item) => item.id === existing.id
            );
            if (existingIndex !== -1) {
              next[existingIndex] = updatedRecord;
              activeCodes.set(normalizedCode, updatedRecord);
              hasChanges = true;
            }
          }
        } else {
          const newRecord = {
            id: getNextTempPaCodeId(),
            nonPolicyPaCodeKey: null,
            paCode: normalizedCode,
            effectiveDateTime: normalizedDate,
            endDateTime: null,
            isDeleted: false,
            isNew: true,
          };
          next.push(newRecord);
          activeCodes.set(normalizedCode, newRecord);
          hasChanges = true;
        }
      });
      return next;
    });

    if (hasChanges) {
      dispatch(setHPConfigChanged({ hpConfigChanged: true }));
    }
    setAddPaDialogOpen(false);
  };

  const handlePaDialogClose = () => {
    setAddPaDialogOpen(false);
  };

  const handlePaCodeEditSave = (updatedRecord) => {
    if (!updatedRecord) return;

    setPaCodes((prev) => {
      const idx = prev.findIndex(
        (code) =>
          code.id === updatedRecord.id ||
          (updatedRecord.originalPaCode &&
            code.paCode?.toUpperCase() ===
              String(updatedRecord.originalPaCode).toUpperCase())
      );
      const { originalPaCode, ...rest } = updatedRecord;
      if (idx === -1) {
        const cleaned = prev.filter(
          (c) =>
            !(
              originalPaCode &&
              c.paCode?.toUpperCase() === String(originalPaCode).toUpperCase()
            )
        );
        return [...cleaned, rest];
      }
      const next = [...prev];
      next[idx] = { ...next[idx], ...rest };
      return next;
    });
    setEditingPaCode(null);
    dispatch(setHPConfigChanged({ hpConfigChanged: true }));
  };

  useEffect(() => {
    getContractTypes();

    const fetchHealthPlanDetails = async () => {
      try {
        const response =
          await api.healthPlanConfig.fetchHealthPlanConfigDetail(hpcKey);
        const healthPlanDetail = response.data;
        setFormData({
          ...healthPlanDetail,
          contractTypes: {
            gtm: healthPlanDetail.gtmIndicator,
            pgtm: healthPlanDetail.pgtmIndicator,
            rtm: healthPlanDetail.rtmIndicator,
            srx: healthPlanDetail.srxIndicator,
          },
          contacts: {
            coding: healthPlanDetail.codingUser || "",
            translation: healthPlanDetail.translationUser || "",
            configuration: healthPlanDetail.configurationUser || "",
            accountManager: healthPlanDetail.managerUser || "",
          },
        });
        setPaCodes(mapPaCodesFromApi(healthPlanDetail.nonPolicyPaCodes));
        setEditingPaCode(null);
        if (healthPlanDetail.logoInByte) {
          setLogo(`data:image/jpeg;base64,${healthPlanDetail.logoInByte}`);
        }
        // Fetch the list of users for the dropdowns
        const usersResponse = await api.healthPlanConfig.fetchAllUsers();
        setUsers(usersResponse.data);
      } catch (error) {
        dispatch(
          setDialog({
            message: error.response?.data?.errors,
            title: error.code + ":: " + error.message,
          })
        );
        handleClose();
      }
    };

    if (hpcKey) {
      fetchHealthPlanDetails();
    }
  }, [hpcKey]);

  useEffect(() => {
    if (
      formData?.contractTypes &&
      Object.keys(formData.contractTypes).length > 0 &&
      initialContractTypesRef.current === null
    ) {
      // take the snapshot ONCE
      initialContractTypesRef.current = formData.contractTypes;
    }
  }, [formData]);

  const handleCancelClick = () => {
    if (adoptionSaved) {
      setCancelDialogOpen(true);
    } else {
      handleClose();
    }
  };

  const handleConfirmCancel = () => {
    setCancelDialogOpen(false);
    handleClose();
  };

  const handleKeepEditing = () => {
    setCancelDialogOpen(false);
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogo(reader.result);
      };
      reader.readAsDataURL(file);
      setLogoFile(file);
    }
  };

  const handleSave = async () => {
    if (!editable) {
      return;
    }

    try {
      if (formData.healthPlanDisplayName === "")
        throw new Error("Please enter Health Plan Display Name");
      dispatch(setLoading(true));

      formData.codingUser = formData.contacts.coding;
      formData.translationUser = formData.contacts.translation;
      formData.configurationUser = formData.contacts.configuration;
      formData.managerUser = formData.contacts.accountManager;
      formData.gtmIndicator = formData.contractTypes.gtm;
      formData.pgtmIndicator = formData.contractTypes.pgtm;
      formData.rtmIndicator = formData.contractTypes.rtm;
      formData.srxIndicator = formData.contractTypes.srx;

      let updatedData = { ...formData };
      updatedData.nonPolicyPaCodes = paCodes.map((code) => ({
        nonPolicyPaCodeKey: code.nonPolicyPaCodeKey,
        paCode: code.paCode,
        effectiveDateTime: formatDateForPayload(code.effectiveDateTime),
        endDateTime: code.endDateTime
          ? formatDateForPayload(code.endDateTime)
          : null,
        isDeleted: Boolean(code.isDeleted),
      }));

      if (logoFile) {
        updatedData.logoInByte = logo
          ? logo.split(",")[1]
          : formData.logoInByte;
        updatedData.logoFileName = logoFile.name;
      }

      const response = await api.healthPlanConfig.updateHealthPlanConfig(
        hpcKey,
        updatedData
      );
      if (response.status === 200) {
        dispatch(setHPConfigChanged({ hpConfigChanged: false }));
        dispatch(
          setDialog({
            message: "Health Plan Configuration saved successfully.",
            title: "Success",
          })
        );
        navigate(-1);
      }
    } catch (error) {
      dispatch(
        setDialog({ message: formErrorStrfromObj(error), title: "Error" })
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

  const ContactSelectRow = ({ contactType, formData, setFormData, users }) => {
    const actualContactType =
      contactType === "accountManager" ? "manager" : contactType;

    const filteredUsers = users.filter((user) =>
      user.poetUserAttributes
        .map((attr) => attr.toLowerCase())
        .includes(actualContactType.toLowerCase())
    );

    const validUserKeys = filteredUsers?.map((user) => user.userKey);
    const selectedValue = formData?.contacts?.[contactType] ?? "";
    const safeValue = validUserKeys?.includes(selectedValue)
      ? selectedValue
      : "";

    const handleChange = (value) => {
      setFormData({
        ...formData,
        contacts: {
          ...formData.contacts,
          [contactType]: value,
        },
      });
    };

    return (
      <Stack direction="row" spacing={1} key={contactType}>
        <CustomTypography>
          {contactType.charAt(0).toUpperCase() + contactType.slice(1)}
        </CustomTypography>
        <FormControl sx={{ flex: 1 }}>
          <CustomSelect
            value={safeValue}
            onChange={(e) => handleChange(e.target.value)}
          >
            {filteredUsers.map((user) => (
              <MenuItem key={user.userKey} value={user.userKey}>
                {user.userFirstName} {user.userLastName}
              </MenuItem>
            ))}
          </CustomSelect>
        </FormControl>
      </Stack>
    );
  };

  const handleContractTypeChange = (key, newValue) => {
    setFormData((prev) => ({
      ...prev,
      contractTypes: {
        ...prev.contractTypes,
        [key]: newValue,
      },
    }));
  };

  const isImplOrActive = (v) =>
    ["implementation", "active"].includes(
      String(v || "")
        .toLowerCase()
        .trim()
    );

  // Helper to check if any contract type is "Implementation" or "Active"
  const hasImplementation = Object.values(formData?.contractTypes || {}).some(
    isImplOrActive
  );

  const activePaCodes = paCodes.filter((code) => !code.isDeleted);

  return (
    <>
      {!formData ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <CircularProgress sx={{ marginRight: "10px" }} />
          <Typography variant="body1" sx={{ marginTop: "0px" }}>
            Loading configuration details...
          </Typography>
        </Box>
      ) : (
        <>
          <Stack>
            <Stack direction="row" gap={4} alignItems="center">
              <Stack
                direction={"row"}
                sx={{ width: "50%" }}
                alignItems="center"
              >
                <CustomTypography fontWeight="bold">
                  Health Plan Display Name *
                </CustomTypography>
                <CustomTextField
                  required
                  value={formData.healthPlanDisplayName}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      healthPlanDisplayName: e.target.value,
                    })
                  }
                  sx={{ flex: 3, marginLeft: 1 }}
                />
              </Stack>

              <Typography fontWeight="bold">Logo</Typography>
              <Button
                variant="contained"
                onClick={() =>
                  document.getElementById("logo-upload-input").click()
                }
              >
                <UploadIcon />
              </Button>
              <input
                id="logo-upload-input"
                type="file"
                hidden
                onChange={handleLogoUpload}
              />
              {logo ? (
                <Box
                  component="img"
                  src={logo}
                  alt="Logo Preview"
                  sx={{
                    width: 200,
                    height: 50,
                    border: false,
                    objectFit: "contain",
                  }}
                />
              ) : (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ flex: 0.5 }}
                >
                  Logo Preview
                </Typography>
              )}
              <Stack direction="row" gap={2} flex={1} justifyContent="end">
                <Button onClick={handleCancelClick}>Cancel</Button>
                {editable && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                  >
                    Save
                  </Button>
                )}
              </Stack>
            </Stack>
            <Stack
              paddingTop={2}
              direction="row"
              gap={4}
              justifyContent="space-between"
            >
              <Stack direction="column" spacing={1} width={"50%"}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <CustomTypography fontWeight="bold">
                    Contract Type(s) *
                  </CustomTypography>
                  {initialContractTypesRef?.current && (
                    <EditHPCreateAdoptionDecisions
                      disabled={!hasImplementation}
                      hpKey={Number(formData.healthPlanKey)}
                      contractTypesSelection={formData.contractTypes}
                      initialContractTypesRef={initialContractTypesRef}
                      contractTypes={contractTypes}
                      onAdoptionDecisionsSaved={() => setAdoptionSaved(true)}
                    />
                  )}
                </Box>
                <Grid container spacing={1} columns={12} sx={{ width: "100%" }}>
                  {contractTypes.map((type) => (
                    <Grid
                      key={type.policyTypeKey}
                      sx={{
                        border: "1px solid #ccc",
                        padding: "2px",
                        gridColumn: "span 3",
                      }}
                    >
                      <FormControl component="fieldset">
                        <FormLabel
                          component="legend"
                          sx={{
                            borderBottom: "1px solid #ccc",
                            paddingBottom: "2px",
                          }}
                        >
                          {type.policyType.toUpperCase()}
                        </FormLabel>
                        <RadioGroup
                          value={
                            formData.contractTypes[
                              type.policyType.toLowerCase()
                            ] || ""
                          }
                          onChange={(e) =>
                            handleContractTypeChange(
                              type.policyType.toLowerCase(),
                              e.target.value
                            )
                          }
                        >
                          {["No", "Implementation", "Active", "Inactive"].map(
                            (option) => (
                              <FormControlLabel
                                key={option}
                                value={option}
                                control={<Radio />}
                                label={
                                  <Box
                                    component="span"
                                    fontWeight={
                                      formData.contractTypes[
                                        type.policyType.toLowerCase()
                                      ] === option
                                        ? "bold"
                                        : "normal"
                                    }
                                  >
                                    {option}
                                  </Box>
                                }
                              />
                            )
                          )}
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </Stack>

            <Stack
              paddingTop={2}
              direction="row"
              gap={4}
              justifyContent="space-between"
            >
              <ActiveLineOfBusiness
                formData={formData}
                setFormData={setFormData}
                editable={editable}
              />
              <Stack
                direction="column"
                spacing={1}
                sx={{ flex: 1, maxWidth: "50%" }}
              >
                <SectionBox
                  title="Health Plan PA Codes"
                  items={activePaCodes}
                  onDelete={editable ? deletePaCode : undefined}
                  onEdit={
                    editable
                      ? (record) => setEditingPaCode({ ...record })
                      : undefined
                  }
                  onAdd={editable ? () => setAddPaDialogOpen(true) : undefined}
                  addDisabled={!editable}
                />
              </Stack>
            </Stack>
          </Stack>
        </>
      )}
      {isAddPaDialogOpen && (
        <AddPaCodesDialog
          onCancel={handlePaDialogClose}
          onSave={handlePaCodesSave}
        />
      )}
      {editingPaCode && (
        <EditPaCodeDialog
          record={editingPaCode}
          existingCodes={activePaCodes
            .filter((code) => code.id !== editingPaCode.id)
            .map((code) => code.paCode)}
          onCancel={() => setEditingPaCode(null)}
          onSave={handlePaCodeEditSave}
        />
      )}
      <Dialog open={cancelDialogOpen} onClose={handleKeepEditing}>
        <DialogTitle>Adoption Decisions Saved</DialogTitle>
        <DialogContent>
          Some changes to Adoption Decisions have already been saved on the
          server and will not be reverted if you cancel. Are you sure you want
          to cancel?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleKeepEditing}>Keep Editing</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmCancel}
          >
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditHealthPlanModal;
